import { useMemo } from 'react'

import { F0Box, F0Heading, F0Text } from '@factorialco/f0-react'
import { Textarea } from '@factorialco/f0-react/dist/experimental'

import type { NumerationState } from './index'
import {
  LINE_ITEM_SEEDS,
  computeFooterTotals,
  computeRowTotals,
  formatCurrency,
  formatNumericField,
} from './previewSeeds'
import { previewLineItemText, previewValue, sectionLabel, totalsLabel } from './translations'
import { SECTION_ORDER, toGridColumns, type MergedField, type SectionType } from './types'

const LOREM_IPSUM = 'Lorem ipsum'
const MIN_PO_NUMBER_PADDING = 5
const FALLBACK_PO_NUMBER_SEED = 1

/**
 * Formats a PO number preview using the current numeration config.
 * Uses the template's `nextNumber` (the value being edited) and dynamic
 * padding: max(digit count, MIN_PO_NUMBER_PADDING). e.g. 1 → "PO-00001".
 */
function formatPoNumberPreview(numeration: NumerationState): string {
  const seed = numeration.nextNumber ?? FALLBACK_PO_NUMBER_SEED
  const digits = String(seed)
  const padding = Math.max(digits.length, MIN_PO_NUMBER_PADDING)
  const padded = digits.padStart(padding, '0')

  if (numeration.prefix) {
    return `${numeration.prefix}-${padded}`
  }
  return padded
}

/** Preview value for a field: custom text → lorem ipsum, custom select → first option, predefined → i18n seed. */
function fieldPreviewValue(field: MergedField, numeration: NumerationState): string {
  if (!field.predefined) {
    if (field.fieldType === 'select' && field.options.length > 0) {
      return field.options[0] || '-'
    }
    return LOREM_IPSUM
  }
  if (field.fieldKey === 'po_number') {
    return formatPoNumberPreview(numeration)
  }
  return previewValue(field.fieldKey)
}

type Props = {
  fields: MergedField[]
  numeration: NumerationState
}

function MainContent({ fields, numeration }: Props) {
  const visibleBySection = useMemo(() => {
    const grouped = new Map<SectionType, MergedField[]>()
    for (const section of SECTION_ORDER) {
      grouped.set(section, [])
    }
    for (const field of fields) {
      if (!field.visible) continue
      const sectionFields = grouped.get(field.sectionType)
      if (sectionFields) {
        sectionFields.push(field)
      }
    }
    for (const sectionFields of grouped.values()) {
      sectionFields.sort((a, b) => a.position - b.position)
    }
    return grouped
  }, [fields])

  return (
    <F0Box display="flex" flexDirection="column" gap="xl" padding="lg">
      {SECTION_ORDER.map((section) => {
        const sectionFields = visibleBySection.get(section) ?? []
        if (sectionFields.length === 0) return null

        if (section === 'line_item_columns') {
          return (
            <LineItemsPreview
              key={section}
              section={section}
              fields={sectionFields}
              numeration={numeration}
            />
          )
        }

        if (section === 'notes_and_delivery') {
          return (
            <NotesPreview
              key={section}
              section={section}
              fields={sectionFields}
              numeration={numeration}
            />
          )
        }

        return (
          <KeyValuePreview
            key={section}
            section={section}
            fields={sectionFields}
            numeration={numeration}
          />
        )
      })}
    </F0Box>
  )
}

/** General Information & Vendor Contact — title outside, then bordered key-value grid */
function KeyValuePreview({
  section,
  fields,
  numeration,
}: {
  section: SectionType
  fields: MergedField[]
  numeration: NumerationState
}) {
  return (
    <F0Box display="flex" flexDirection="column" gap="sm">
      <F0Heading content={sectionLabel(section)} as="h3" />
      <F0Box
        border="default"
        borderColor="secondary"
        borderRadius="sm"
        display="flex"
        flexDirection="column"
        divider="y"
        dividerColor="secondary"
        background="primary"
      >
        {fields.map((field) => (
          <F0Box
            key={field.key}
            display="grid"
            columns="3"
            paddingX="md"
            paddingY="sm"
            alignItems="center"
          >
            <F0Box colSpan="1">
              <F0Text content={field.displayLabel} variant="description" />
            </F0Box>
            <F0Box colSpan="2">
              <F0Text content={fieldPreviewValue(field, numeration)} variant="body" />
            </F0Box>
          </F0Box>
        ))}
      </F0Box>
    </F0Box>
  )
}

/** Line Item Columns — title outside, then bordered grid table with aligned columns */
function LineItemsPreview({
  section,
  fields,
  numeration,
}: {
  section: SectionType
  fields: MergedField[]
  numeration: NumerationState
}) {
  const columnCount = toGridColumns(fields.length)

  const visibleFieldKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const f of fields) {
      keys.add(f.fieldKey)
    }
    return keys
  }, [fields])

  const rowData = useMemo(
    () =>
      LINE_ITEM_SEEDS.map((seed) => ({
        seed,
        totals: computeRowTotals(seed, visibleFieldKeys),
      })),
    [visibleFieldKeys]
  )

  const footerTotals = useMemo(
    () => computeFooterTotals(LINE_ITEM_SEEDS, visibleFieldKeys),
    [visibleFieldKeys]
  )

  return (
    <F0Box display="flex" flexDirection="column" gap="sm">
      <F0Heading content={sectionLabel(section)} as="h3" />
      <F0Box
        border="default"
        borderColor="secondary"
        borderRadius="sm"
        display="flex"
        flexDirection="column"
        divider="y"
        dividerColor="secondary"
        background="primary"
      >
        {/* Header row */}
        <F0Box display="grid" columns={columnCount}>
          {fields.map((field) => (
            <F0Box key={field.key} paddingX="sm" paddingY="sm">
              <F0Text
                content={field.required ? `${field.displayLabel} *` : field.displayLabel}
                variant="description"
              />
            </F0Box>
          ))}
        </F0Box>
        {/* Sample data rows */}
        {rowData.map((row, rowIndex) => (
          <F0Box key={`seed-${String(rowIndex)}`} display="grid" columns={columnCount}>
            {fields.map((field) => {
              const numericValue = formatNumericField(field.fieldKey, row.seed, row.totals)
              let cellContent: string
              if (field.fieldKey === 'line_number') {
                cellContent = String(rowIndex + 1)
              } else if (numericValue !== null) {
                cellContent = numericValue
              } else if (!field.predefined) {
                cellContent = fieldPreviewValue(field, numeration)
              } else {
                cellContent = previewLineItemText(rowIndex, field.fieldKey)
              }
              return (
                <F0Box key={field.key} paddingX="sm" paddingY="sm">
                  <F0Text content={cellContent} variant="body" />
                </F0Box>
              )
            })}
          </F0Box>
        ))}
      </F0Box>
      {/* Totals outside the bordered table */}
      <F0Box display="flex" gap="xl" justifyContent="end" paddingX="sm">
        <F0Box display="flex" flexDirection="column" alignItems="end" gap="xs">
          <F0Text content={totalsLabel('net')} variant="description" />
          <F0Text content={formatCurrency(footerTotals.net)} variant="body" />
        </F0Box>
        <F0Box display="flex" flexDirection="column" alignItems="end" gap="xs">
          <F0Text content={totalsLabel('tax')} variant="description" />
          <F0Text content={formatCurrency(footerTotals.tax)} variant="body" />
        </F0Box>
        <F0Box display="flex" flexDirection="column" alignItems="end" gap="xs">
          <F0Text content={totalsLabel('total')} variant="description" />
          <F0Text content={formatCurrency(footerTotals.total)} variant="body" />
        </F0Box>
      </F0Box>
    </F0Box>
  )
}

/** Notes & Delivery — title outside, then each field as a disabled Textarea */
function NotesPreview({
  section,
  fields,
  numeration,
}: {
  section: SectionType
  fields: MergedField[]
  numeration: NumerationState
}) {
  return (
    <F0Box display="flex" flexDirection="column" gap="sm">
      <F0Heading content={sectionLabel(section)} as="h3" />
      {fields.map((field) => (
        <Textarea
          key={field.key}
          label={field.displayLabel}
          value={fieldPreviewValue(field, numeration)}
          rows={3}
          disabled
        />
      ))}
    </F0Box>
  )
}

export default MainContent
